import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '../../../../shared/hooks/useDebounce';
import { useStableListParams } from '../../../../shared/hooks/useStableListParams';
import { organizationApi } from '../services/contactsApi';
import type { OrganizationBulkScope } from '../services/contactsApi';
import type { OrganizationCreate, OrganizationUpdate, ListOrganizationsParams } from '../types';

// See `hooks/useContacts.ts` — same re-export, same reason.
export { organizationKeys } from './queryKeys';
import { organizationKeys } from './queryKeys';

/**
 * Extract a bare host from a contact email (e.g. `fred@Acme.com` → `acme.com`).
 * Returns '' for personal/free-mail providers and anything not domain-shaped,
 * so we never suggest an organization for gmail.com / outlook.com etc.
 */
// Intentionally non-exhaustive: the common free-mail providers worth filtering
// so we never suggest an organization for a personal address. A miss here only
// costs one no-op match-domain lookup that returns nothing.
const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com',
  'yahoo.com', 'ymail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
  'proton.me', 'protonmail.com', 'gmx.com', 'mail.com', 'zoho.com', 'msn.com',
]);

export function domainFromEmail(email: string | undefined | null): string {
  if (!email) return '';
  const at = email.indexOf('@');
  if (at === -1) return '';
  const host = email.slice(at + 1).trim().toLowerCase();
  // Require a domain-shaped host with a TLD before we bother the backend.
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(host)) return '';
  if (FREE_EMAIL_DOMAINS.has(host)) return '';
  return host;
}

/**
 * Suggest an existing organization matching the domain of a contact's email.
 * Debounced; only queries once the email contains a real company domain.
 */
export function useOrganizationDomainMatch(email: string | undefined, enabled = true) {
  const domain = domainFromEmail(email);
  const debouncedDomain = useDebounce(domain, 400);
  const query = useQuery({
    queryKey: organizationKeys.domainMatch(debouncedDomain),
    queryFn: () => organizationApi.matchByDomain(debouncedDomain),
    enabled: enabled && debouncedDomain.length > 0,
    staleTime: 60_000,
  });
  return { domain: debouncedDomain, match: query.data ?? null, isFetching: query.isFetching };
}

export function useOrganizations(params?: ListOrganizationsParams, options?: { enabled?: boolean }) {
  // Every field participates, by construction — see `useStableListParams`. The
  // hand-listed dep array this replaces had omitted `staleness`, exactly as its
  // contacts twin did (WI 7800e951).
  const stableParams = useStableListParams(params);

  return useQuery({
    queryKey: organizationKeys.list(stableParams),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => organizationApi.list(stableParams),
    enabled: options?.enabled ?? true,
  });
}

export function useOrganization(id: string) {
  return useQuery({
    queryKey: organizationKeys.detail(id),
    queryFn: () => organizationApi.get(id),
    enabled: !!id,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OrganizationCreate) => organizationApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OrganizationUpdate }) => organizationApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

/**
 * Deletion-impact preview for the delete dialog — what would be orphaned, and
 * what still blocks. See `useContactDeletionImpact` for why it is not cached.
 */
export function useOrganizationDeletionImpact(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...organizationKeys.detail(id ?? ''), 'deletion-impact'],
    queryFn: () => organizationApi.deletionImpact(id as string),
    enabled: enabled && !!id,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useArchiveOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationApi.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useUnarchiveOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationApi.unarchive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useBulkDeleteOrganizations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope: OrganizationBulkScope) => organizationApi.bulkDelete(scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useCascadeRelationship() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      organizationId,
      contactRelationship,
      contactIds,
    }: {
      organizationId: string;
      contactRelationship: string[];
      contactIds?: string[];
    }) => organizationApi.cascadeRelationship(organizationId, contactRelationship, contactIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useBulkArchiveOrganizations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scope: OrganizationBulkScope) => organizationApi.bulkArchive(scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}
